import { Controller } from "stimulus";
import Hls from "hls.js";
import mux from "mux-embed";
import Plyr from "@supercast/plyr";
import { SupercastTimeUpdateEvent } from "utils/events";
import { maybeConnectBridge } from "utils/hotwire";

/** 🚨🚨🚨🚨 WARNING: This controller has bridge component counterparts. 🚨🚨🚨🚨
 *
 * This means that the functionality provided below is handled natively in the mobile apps.
 * Ensure that any changes to this controller are also reflected in the bridge component counterparts, and
 * tested in both Android and iOS.
 *
 * You can find the bridge component counterparts (one per stream type) in:
 * app/components/media/bridge/video_player_controller.js
 * app/components/media/bridge/live_player_controller.js
 * app/components/media/bridge/premiere_player_controller.js
 */

// How close to the party playhead counts as "caught up" - fast-forward is disabled inside this band.
const premiereAtEdgeToleranceSeconds = 1;

// AWS HLS only (see awsHlsValue): the master manifest only lists rungs that have actually
// finished encoding, so a session that starts before every rung is done won't see a later one appear on its own
// - this polls the master manifest directly and does a clean reload when it's grown.
const masterManifestPollIntervalMs = 45_000;
const masterManifestMaxPollAttempts = 40; // ~30 minutes total - stop rather than poll forever if a rung never finishes
const masterManifestRungTotal = 5;

// hls.js fatal NETWORK_ERROR retry ladder: 1s, 2s, 4s, 8s, 8s, 8s - a ~30 second budget. Any
// successfully loaded fragment resets it (see FRAG_LOADED in createHls), so this is per-blip
// rather than per-session: the old 3-attempt/6-second ladder let one brief connectivity blip
// permanently kill a 2.5-hour premiere 29 minutes in.
//
// Deliberately the same budget for live, even though live's terminal action is the cheap page
// reload rather than an MP4 fallback, so a genuine live failure now black-screens ~30s instead
// of ~6s. That is the accepted trade: the reload re-initialises chat, tips and polls, so paying
// it for a transient blip is worse for the viewer than a longer buffering spinner.
const hlsNetworkErrorMaxRetries = 6;
const hlsNetworkErrorMaxBackoffMs = 8_000;

// Which playback branch of loadHlsUrl() produced the Mux view. Reported as custom_1 so the three
// branches can be told apart in the dashboard: before this, Mux was only attached on the hls.js
// branch, which made every WebKit (native HLS) and MP4-fallback view invisible.
const muxBranchHlsJs = "hlsjs";
const muxBranchNativeHls = "native-hls";
const muxBranchProgressive = "progressive-mp4";

// Session cookie, so theater survives Turbo navigations and is shared across tabs, but dies with
// the browser. Raw document.cookie because js-cookie is only vendored for the sprockets pipeline.
// Must match CookieHelper::THEATER_MODE - nothing enforces the two stay in sync.
const THEATER_COOKIE = "theater_mode";

function theaterCookieEnabled() {
  return document.cookie.split(";").some(c => c.trim() === `${THEATER_COOKIE}=1`);
}

function writeTheaterCookie(enabled) {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${THEATER_COOKIE}=${enabled ? "1" : "0"}; path=/; SameSite=Lax${secure}`;
}

export default class extends Controller {
  static targets = [
    "controlsTemplate",
    "videoContainer",
    "popoutPlaceholder",
    "castPlaceholder",
    "castButton",
    "floatingButton",
    "nonFloatingButton",
    "theaterButton",
    "playback1",
    "playback1_5",
    "playback2"
  ]

  static values = {
    timestamp: Number,
    markers: Array,
    floating: Boolean,
    fileType: String,
    autoplay: Boolean,
    allowNativePlay: Boolean,
    cdn: String,
    videoTitle: String,
    userId: String,
    streamType: String,
    liveUrl: String,
    muxEnvKey: String,
    publishedAtMs: Number,
    serverNowMs: Number,
    duration: Number,
    awsHls: Boolean,
    blankVideoUrl: String
  }

  async connect() {
    if (this.allowNativePlayValue && maybeConnectBridge(this.element, this.bridgeComponent(), this.bridgeControllerName())) {
      return;
    }

    // Inside the native shell this player is always CSS-hidden (hotwire-native:hidden), so
    // when the hand-off to native didn't happen (old binary without the bridge component,
    // or native play disabled) there is nothing to show: skip web init entirely rather than
    // buffering HLS segments and firing Mux QoS beacons behind an invisible element.
    if (document.querySelector("[data-hotwire-native]")) {
      return;
    }

    this.muxPlayerInitTime = mux.utils.now();
    this.video = this.element.querySelector('video');

    // Snapshot clock skew once so the shared playhead stays on server time.
    if (this.isPremiere()) {
      this.clockOffsetMs = this.serverNowMsValue - Date.now();
    }

    this.initializePlayer();

    if (this.isPremiere()) {
      this.premiereClampForwardSeeks();
      this.startPremiereEdgeUpdates();
    }
    this.boundReadyToPlay = this.readyToPlay.bind(this);

    if (!this.isFloating()) {
      this.floatingButtonTargets.forEach(button => button.classList.add("hidden"));
      this.nonFloatingButtonTargets.forEach(button => button.classList.remove("hidden"));
    } else {
      this.floatingButtonTargets.forEach(button => button.classList.remove("hidden"));
      this.nonFloatingButtonTargets.forEach(button => button.classList.add("hidden"));
      // toggleTheater() no-ops while floating, so the control must not be left visible-but-dead
      // in the small floating player - "!hidden" overrides its own "lg:inline-flex" without
      // disturbing that responsive class (unlike the shared nonFloatingButton "hidden" toggle above).
      this.theaterButtonTargets.forEach(button => button.classList.add("!hidden"));
    }

    this.syncTheaterState();

    this.boundOnTimeUpdate = this.onTimeUpdate.bind(this);
    window.addEventListener("supercast-timeupdate", this.boundOnTimeUpdate);
    this.boundRestoreTheaterState = this.restoreTheaterState.bind(this);
    window.addEventListener("pageshow", this.boundRestoreTheaterState);

    if (!this.video.src) {
      this.initializeVideo();
      if (this.hasCastButtonTarget) {
        this.initChromecast();
      }
    }
  }

  isLive() {
    return this.streamTypeValue === "live";
  }

  // VOD path everywhere except the shared-playhead seek + forward clamp below.
  isPremiere() {
    return this.streamTypeValue === "premiere";
  }

  // The bridge component name doubles as the native capability check: old app installs
  // don't declare the live/premiere names, so those pages keep today's behavior there.
  bridgeComponent() {
    if (this.isLive()) return "live-player";
    if (this.isPremiere()) return "premiere-player";
    return "video-player";
  }

  // The bridge controllers live under app/components/media/bridge/, so their
  // glob-registered Stimulus identifier is always media--bridge--<component>.
  bridgeControllerName() {
    return `media--bridge--${this.bridgeComponent()}`;
  }

  serverNow() {
    return Date.now() + this.clockOffsetMs;
  }

  premiereLivePos() {
    return Math.min(Math.max((this.serverNow() - this.publishedAtMsValue) / 1000, 0), this.durationValue);
  }

  // Furthest position a premiere viewer may seek to right now. Saturates at the full duration
  // once the premiere has elapsed, so the afterparty is ordinary free scrubbing.
  premiereMaxSeek() {
    return this.isPremiere() ? this.premiereLivePos() : null;
  }

  // Clamp only forward seeks past the shared edge (rewind/pause OK).
  clampToPremiereEdge() {
    const livePos = this.premiereLivePos();

    // Premiere over: stop clamping, matching iOS MediaPlaybackService and Android PlayerController guards.
    if (livePos >= this.durationValue) {
      return;
    }

    if (this.video.currentTime > livePos + 1.5) {
      this.video.currentTime = livePos;
    }
  }

  // Premiere: clamp forward seeks to the shared party playhead, so the jump lands where the
  // 1.5s snap-back clamp would leave it anyway, with no visible bounce. `forward` is a Plyr
  // class field (an own instance property), so overriding it covers every caller at once: the
  // fast-forward button, the ArrowRight keyboard shortcut, and our Media Session handler.
  premiereClampForwardSeeks() {
    const plyrForward = this.player.forward;
    this.player.forward = (seekTime) => {
      const max = this.premiereMaxSeek();
      if (max === null) {
        plyrForward(seekTime);
        return;
      }
      const step = typeof seekTime === "number" ? seekTime : this.player.config.seekTime;
      const current = this.player.currentTime;
      // Floor at current position: if currentTime is already past max (the reactive clamp
      // tolerates up to 1.5s ahead before snapping back), a forward seek must never move
      // the playhead backward.
      this.player.currentTime = Math.max(current, Math.min(current + step, max));
      this.updateFastForwardState();
    };
  }

  // Premiere only: the fast-forward button is dead weight once you are caught up to the party,
  // so disable it there rather than leaving a tap that silently does nothing.
  updateFastForwardState() {
    const button = this.player.elements.buttons?.fastForward;
    const max = this.premiereMaxSeek();
    if (!button || max === null) return;

    const atEdge = max - this.player.currentTime <= premiereAtEdgeToleranceSeconds;
    if (atEdge && document.activeElement === button) {
      // Disabling a focused control force-blurs to <body>, outside Plyr's container-scoped
      // keydown listener, which would silently kill every keyboard shortcut (ArrowRight
      // included). Move focus to the play button first so shortcuts keep working. Uses
      // elements.container, not elements.controls - the latter can go null once the player
      // is relocated into the floating player (see injectControls), the former is the same
      // stable lookup root already used for the PiP button above.
      this.player.elements.container?.querySelector('[data-plyr="play"]')?.focus();
    }
    button.disabled = atEdge;
    button.classList.toggle("opacity-40", atEdge);
    button.classList.toggle("cursor-not-allowed", atEdge);
  }

  startPremiereEdgeUpdates() {
    this.updateFastForwardState();
    this.premiereEdgeInterval = setInterval(() => this.updateFastForwardState(), 1000);
  }

  stopPremiereEdgeUpdates() {
    clearInterval(this.premiereEdgeInterval);
    this.premiereEdgeInterval = null;
  }

  // Live is "purely live" - resuming from a pause snaps to the live edge instead of
  // crawling forward from behind. Only seeks when actually behind, so it's a no-op at the edge.
  seekToLiveEdge() {
    const liveEdge = this.liveEdgePosition();
    if (liveEdge != null && liveEdge - this.video.currentTime > 1) {
      this.video.currentTime = liveEdge;
    }
  }

  liveEdgePosition() {
    if (this.hls && Number.isFinite(this.hls.liveSyncPosition)) {
      return this.hls.liveSyncPosition;
    }
    const seekable = this.video.seekable;
    return seekable && seekable.length > 0 ? seekable.end(seekable.length - 1) : null;
  }

  // Low-latency mode is required for LL-HLS live playback and is a no-op for VOD.
  //
  // xhrSetup: AWS media is served from a CloudFront domain, authorized via signed cookies;
  // cookies only ride along on cross-origin XHRs that opt in via withCredentials.
  // Only opt in when awsHlsValue is true (never for Bunny-backed)
  createHls() {
    const hls = new Hls({
      lowLatencyMode: true,
      backBufferLength: 90,
      // Don't pull a rung bigger than the element it renders into - a 411px phone was spending
      // 76% of a sampled view downscaling the 5 Mbps 1080p rung. hls.js re-evaluates on
      // resize/fullscreen, so going fullscreen still unlocks the higher rungs.
      capLevelToPlayerSize: true,
      xhrSetup: (xhr) => {
        if (this.awsHlsValue) {
          xhr.withCredentials = true;
        }
      }
    });

    this.networkErrorRetries = 0;
    this.mediaErrorRecoveries = 0;
    this.hlsRetryTimeoutId = null;
    hls.on(Hls.Events.ERROR, this.handleHlsError.bind(this));
    // A fragment loading fine means playback actually recovered, not just that hls.js stopped
    // erroring - reset both budgets so a later, unrelated blip gets its own full retry ladder.
    hls.on(Hls.Events.FRAG_LOADED, () => {
      this.networkErrorRetries = 0;
      this.mediaErrorRecoveries = 0;
    });

    return hls;
  }

  async initializeVideo() {
    this.hls = this.createHls();

    // Registered before any mux.monitor() call so it runs ahead of mux-embed's own
    // automaticErrorTracking listener on this element (mux binds its listeners at monitor() time):
    // the dimensions below are attached to the very beacon mux is about to send for the error.
    this.boundRecordMediaError = this.recordMediaError.bind(this);
    this.video.addEventListener('error', this.boundRecordMediaError);

    this.video.addEventListener('canplay', this.boundReadyToPlay);

    this.originalUrl = this.element.getAttribute('data-original-url');

    // Live streams have no app playlist/original fallback - load the signed manifest directly.
    if (this.isLive()) {
      this.hlsUrl = this.liveUrlValue;
      this.loadHlsUrl();
      return;
    }

    // Try to fetch the hls playlist url, if we can't find it then fall back to the original
    this.hlsUrl = this.element.getAttribute('data-playlist-url');

    // Premiere's hlsUrl is a cross-origin CDN URL, so a thrown HEAD (CORS/network) must fall back, not hang.
    let response;
    try {
      response = await fetch(this.hlsUrl, { method: 'HEAD', credentials: this.awsHlsValue ? 'include' : 'same-origin' });
    } catch {
      this.loadOriginalUrl();
      return;
    }

    if (response.ok) {
      this.loadHlsUrl();
      if (this.awsHlsValue) {
        this.startMasterManifestPolling();
      }
    } else {
      this.loadOriginalUrl();
    }
  }

  loadHlsUrl(withMuxMonitoring = true) {
    /**
     * A note on this conditional: We check if airplay is supported because HLS.js
     * will populate the source of the video with blobs that are not supported by
     * Airplay. Every browser that IS capable of Airplay will support HLS natively though,
     * so we don't need HLS.js in that case.
     *
     * Why not just look for native HLS support and prioritize that? Because some versions
     * of Android have shaky support for HLS, so depsite saying they support it, it causes
     * freezing issues when seeking using native HLS (this is inconsistent so if you're changing
     * this code, test extensively on Android).
     */
    if (Hls.isSupported() && !window.WebKitPlaybackTargetAvailabilityEvent) {
      var video = this.video;
      this.hls.loadSource(this.hlsUrl);
      this.hls.attachMedia(video);
      if (withMuxMonitoring) this.initMuxMonitoring(muxBranchHlsJs);
    } else if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
      const url = this.element.getAttribute('data-playlist-url');
      // Every WebKit browser lands here (Safari + all of iOS) - unmonitored until now, which is why
      // Mux showed zero WebKit views for premieres that certainly had iOS viewers.
      if (withMuxMonitoring) this.initMuxMonitoring(muxBranchNativeHls);
      this.setVideoToHls();
    } else {
      this.loadOriginalUrl();
    }
  }

  // Monitoring is attached here rather than only in loadHlsUrl()'s else-branch because this is
  // reached from three other places too (both initializeVideo() fallbacks, a failed
  // setVideoToHls()) - initMuxMonitoring() is idempotent, so the extra calls are no-ops.
  loadOriginalUrl() {
    // Live renders no data-original-url, so originalUrl is null here - and `video.src = null`
    // coerces to the literal string "null", which resolves against the page URL and guarantees a
    // MEDIA_ERR_SRC_NOT_SUPPORTED. Skip only the assignment: the telemetry below (custom_5
    // original_url:missing) is how this case stays visible.
    if (this.originalUrl) this.video.src = this.originalUrl;
    this.initMuxMonitoring(muxBranchProgressive);
    // Already monitoring another branch that fell through to the MP4 (a failed setVideoToHls;
    // recoverFromFatalHlsError has already reported its own swap) - report the change once.
    if (this.muxBranch !== muxBranchProgressive) this.muxSourceChange(muxBranchProgressive);
    this.recordFallbackSource();

    // Live with nothing to fall back to: reload instead of leaving a blank player. This is
    // reachable without hls.js ever erroring - on WebKit, loadHlsUrl() takes the native branch and
    // a non-OK signed manifest (expired/revoked playback token) lands here from setVideoToHls(),
    // so handleHlsError/recoverFromFatalHlsError never run and nothing else would ever retry. A
    // reload mints a fresh signed manifest, which is the actual fix for an expired token. Last
    // statement in the method on purpose: the beacons above would otherwise race the unload.
    if (!this.originalUrl && this.isLive()) this.reloadOnceForLive();
  }

  // hls.js's documented recovery ladder: retry network loads, then recover media (swap codec on repeat), then fall back.
  handleHlsError(event, data) {
    // Telemetry only: keep hls.js's own classification of the most recent error so a later media
    // element error can be attributed. The ladder below otherwise discards it entirely.
    this.lastHlsError = `${data.type}/${data.details}${data.fatal ? "/fatal" : ""}`;

    if (!data.fatal) return;

    this.muxUpdateData({ custom_3: this.lastHlsError });

    if (data.type === Hls.ErrorTypes.NETWORK_ERROR && this.networkErrorRetries < hlsNetworkErrorMaxRetries) {
      this.networkErrorRetries += 1;
      clearTimeout(this.hlsRetryTimeoutId);
      const backoffMs = Math.min(2 ** (this.networkErrorRetries - 1) * 1000, hlsNetworkErrorMaxBackoffMs);
      this.hlsRetryTimeoutId = setTimeout(() => this.hls.startLoad(), backoffMs);
      return;
    }

    if (data.type === Hls.ErrorTypes.MEDIA_ERROR && this.mediaErrorRecoveries < 2) {
      this.mediaErrorRecoveries += 1;
      if (this.mediaErrorRecoveries === 2) {
        this.hls.swapAudioCodec();
      }
      this.hls.recoverMediaError();
      return;
    }

    this.recoverFromFatalHlsError();
  }

  // Scoped per item (data-item-id, the controller's canonical per-item identity - see muxMetadata),
  // so a stale reload marker from one broken item can't silently suppress recovery on an
  // unrelated live/premiere page loaded next in the same tab.
  reloadGuardKey() {
    return `supercast-video-player-reloaded-after-error:${this.element.getAttribute('data-item-id')}`;
  }

  // The single recovery live has: a fresh page mints a fresh signed manifest. Shared by every live
  // failure path (see recoverFromFatalHlsError and loadOriginalUrl) so they cannot each spend their
  // own reload - one broken item gets at most one reload per session, never a loop. The marker is
  // cleared in readyToPlay() once playback actually starts.
  reloadOnceForLive() {
    if (window.sessionStorage.getItem(this.reloadGuardKey())) return;

    window.sessionStorage.setItem(this.reloadGuardKey(), "1");
    window.location.reload();
  }

  // Recovery exhausted: fall back to original_url (preserving playhead) for premiere/VOD, or a sessionStorage-guarded single reload for live.
  recoverFromFatalHlsError() {
    clearTimeout(this.hlsRetryTimeoutId);
    clearTimeout(this.masterManifestPollTimeoutId);

    // Returns regardless of whether the guard actually let the reload through: live has no MP4
    // fallback, so falling through to the swap below is never right for it.
    if (this.isLive()) {
      this.reloadOnceForLive();
      return;
    }

    const resumePosition = this.video.currentTime;

    // Deliberate source change to the MP4 fallback, not a playback failure: unbind Mux from the
    // hls.js instance about to be destroyed and start a fresh view for the new source, otherwise
    // mux-embed's automaticErrorTracking reports the swap as a MEDIA_ERR_SRC_NOT_SUPPORTED failure.
    this.muxRemoveHls();
    this.muxSourceChange(muxBranchProgressive);

    this.hls.destroy();
    this.hls = null;
    if (resumePosition > 0) {
      this.video.addEventListener('canplay', () => {
        this.video.currentTime = resumePosition;
      }, { once: true });
    }
    this.loadOriginalUrl();
  }

  // Kicks off polling once the initial manifest has loaded successfully - see loadHlsUrl().
  // Grabs its own baseline rung count via a plain fetch (never touching hls.js internals or
  // relying on what hls.js itself parsed), so it starts from a real count instead of assuming 1.
  async startMasterManifestPolling() {
    this.masterManifestPollAttempts = 0;
    this.masterManifestRungCount = await this.fetchManifestRungCount();
    if (this.masterManifestRungCount === null) return; // couldn't read a baseline - don't guess, don't poll

    this.scheduleNextManifestPoll();
  }

  scheduleNextManifestPoll() {
    if (this.masterManifestRungCount >= masterManifestRungTotal) return; // every rung is already there
    if (this.masterManifestPollAttempts >= masterManifestMaxPollAttempts) return; // generous cap reached

    this.masterManifestPollTimeoutId = setTimeout(() => this.pollMasterManifest(), masterManifestPollIntervalMs);
  }

  async pollMasterManifest() {
    this.masterManifestPollAttempts += 1;

    const count = await this.fetchManifestRungCount();
    if (count !== null && count > this.masterManifestRungCount) {
      this.masterManifestRungCount = count;
      this.reloadHlsForNewRungs();
    }

    this.scheduleNextManifestPoll();
  }

  // A plain, uncached GET - counting #EXT-X-STREAM-INF lines is enough to detect growth without
  // parsing the manifest any further. Never throws: a failed fetch just means "try again next tick".
  async fetchManifestRungCount() {
    try {
      const response = await fetch(this.hlsUrl, { credentials: this.awsHlsValue ? 'include' : 'same-origin', cache: 'no-store' });
      if (!response.ok) return null;

      const body = await response.text();
      return (body.match(/#EXT-X-STREAM-INF/g) || []).length;
    } catch {
      return null;
    }
  }

  // A new rung appeared: clean teardown + recreate against the same (now-richer) master URL,
  // restoring playhead/playback state - no inspection of hls.js's level/error internals at all.
  reloadHlsForNewRungs() {
    if (!this.hls) return; // already fell back to loadOriginalUrl (e.g. a fatal error) - nothing to upgrade

    const resumePosition = this.video.currentTime;
    const wasPlaying = !this.video.paused;

    // Read before anything below can move it: muxSourceChange() assigns this.muxBranch, so an
    // unguarded call would flip a native-HLS view to "hlsjs" and then make its own guard - and
    // muxRemoveHls()'s on the next upgrade - wrongly pass.
    const wasOnHlsJsBranch = this.muxBranch === muxBranchHlsJs;

    // Routine rung-ladder reload during a premiere, not an error. Mux was bound to the instance
    // we're replacing, so without the remove/re-add pair the new instance would report nothing;
    // videochange keeps the swap out of the failure numbers. loadHlsUrl(false) below deliberately
    // skips monitor(), which would end this view and start another.
    //
    // All of it is hls.js-branch-only. Polling is started for any awsHls premiere regardless of
    // which branch loadHlsUrl() took, and this.hls is non-null even on the native path
    // (initializeVideo creates it unconditionally), so on WebKit this would otherwise re-label a
    // natively-playing view as "hlsjs" and bind Mux to an instance that was never attachMedia'd.
    // The playback teardown/recreate below still runs on every branch, exactly as before.
    this.muxRemoveHls();

    this.hls.destroy();
    this.hls = this.createHls();

    if (wasOnHlsJsBranch) {
      this.muxSourceChange(muxBranchHlsJs);
      this.muxAddHls();
    }

    this.video.addEventListener('canplay', () => {
      if (resumePosition > 0) this.video.currentTime = resumePosition;
      if (wasPlaying) this.video.play();
    }, { once: true });

    this.loadHlsUrl(false);
  }

  // Airplay doesn't seem to like following redirects - by prefetching the HLS URL and setting the src to that
  // directly we avoid the redirect and Airplay works as expected.
  async setVideoToHls() {
    const response = await fetch(this.hlsUrl, { credentials: this.awsHlsValue ? 'include' : 'same-origin' });
    if (response.ok) {
      this.video.src = response.url;
    } else {
      this.loadOriginalUrl();
    }
  }

  // Idempotent: a second mux.monitor() on the same element tears down the running view (and loses its
  // startup timing) - later source changes go through muxSourceChange() instead.
  initMuxMonitoring(branch) {
    if (this.muxAttached) return;

    this.muxAttached = true;
    this.muxBranch = branch;

    mux.monitor(this.video, {
      debug: false,
      // hls.js instrumentation only belongs on the branch that actually attached it: this.hls is
      // created unconditionally in initializeVideo(), so on the native-HLS/MP4 branches it exists
      // but was never attachMedia'd and would never emit anything.
      ...(branch === muxBranchHlsJs ? { hlsjs: this.hls, Hls: Hls } : {}),
      data: this.muxMetadata()
    });
  }

  // Shared by monitor() and every videochange - an empty videochange payload drops the view's
  // metadata (including startup timing), per mux-embed's docs.
  muxMetadata() {
    return {
      env_key: this.muxEnvKeyValue,
      player_name: "Supercast Video Player",
      player_init_time: this.muxPlayerInitTime,
      // data-item-id (live_stream.id for live, item.id otherwise) is rendered in every mode and is
      // already this controller's canonical per-item identity - see reloadGuardKey() and
      // updateFeedTokenConnection(). It replaces episodeIdValue, which was never rendered by the
      // component, so every beacon reported video_id: 0 and errors couldn't be attributed at all.
      // An Episode id colliding with a LiveStreamSetting id is disambiguated by video_content_type.
      video_id: String(this.element.getAttribute('data-item-id') || ""),
      video_title: this.videoTitleValue,
      video_stream_type: this.streamTypeValue,
      video_cdn: this.cdnValue,
      video_content_type: this.isLive() ? "livestream" : "episode",
      view_client_application_name: "Supercast (Web)",
      custom_1: this.muxBranch
    };
  }

  // An intentional source swap. Mux treats this as a new view rather than a failure of the old one.
  muxSourceChange(branch) {
    if (!this.muxAttached) return;

    this.muxBranch = branch;
    mux.emit(this.video, "videochange", this.muxMetadata());
  }

  muxRemoveHls() {
    if (!this.muxAttached || this.muxBranch !== muxBranchHlsJs) return;

    mux.removeHLSJS(this.video);
  }

  // Branch-guarded like muxRemoveHls(): mux.addHLSJS is meaningless for a view that isn't playing
  // through hls.js, and this.hls exists on every branch, so the guard belongs here rather than
  // only at the call site. (No equivalent guard can live in muxSourceChange() - it is generic, and
  // the native-hls > progressive-mp4 transitions in loadOriginalUrl()/recoverFromFatalHlsError()
  // are legitimate.)
  muxAddHls() {
    if (!this.muxAttached || this.muxBranch !== muxBranchHlsJs) return;

    mux.addHLSJS(this.video, { hlsjs: this.hls, Hls: Hls });
  }

  // Guarded because mux-embed logs an error for any call made before monitor() or after teardown.
  muxUpdateData(data) {
    if (!this.muxAttached) return;

    mux.updateData(this.video, data);
  }

  // mux.destroyMonitor() emits 'destroy' and unbinds mux's own element listeners (mux-embed 5.18.1).
  muxTeardown() {
    if (!this.muxAttached) return;

    this.muxAttached = false;
    mux.destroyMonitor(this.video);
  }

  // A real failure, as opposed to the teardown/source-swap noise above: code 4 here with no
  // preceding videochange/destroy is a genuine MEDIA_ERR_SRC_NOT_SUPPORTED.
  recordMediaError() {
    const code = this.video.error?.code;
    if (!code) return;

    this.muxUpdateData({
      custom_2: `media_error_code:${code}`,
      custom_3: this.lastHlsError || "none"
    });
  }

  // Plyr owns this element and resets its own source, so the src loadOriginalUrl() assigns is not
  // necessarily what the element ends up loading: a Mux single-view trace of a code-4 failure
  // recorded cdn.plyr.io/static/blank.mp4 (Plyr's blankVideo placeholder) as the source. Recording
  // what the element actually resolved to should settle that from the dashboard alone.
  recordFallbackSource() {
    this.muxUpdateData({ custom_5: `original_url:${this.originalUrl ? "present" : "missing"}` });

    if (this.boundRecordFallbackSrcSample) return; // second fallback in the same session - listener already live

    this.fallbackSrcTrail = [];
    this.recordFallbackSrcSample();

    // Deliberately not { once: true }: a Plyr source reset lands AFTER our assignment, so the
    // transition (signed-mp4>plyr-blank) is the evidence - either endpoint on its own is
    // ambiguous. Deduped and capped so the dimension stays short.
    this.boundRecordFallbackSrcSample = this.recordFallbackSrcSample.bind(this);
    this.video.addEventListener('loadstart', this.boundRecordFallbackSrcSample);
  }

  // currentSrc is only populated once the element actually starts loading, hence the loadstart samples.
  recordFallbackSrcSample() {
    const sample = this.describeSource(this.video.currentSrc || this.video.src);
    if (this.fallbackSrcTrail[this.fallbackSrcTrail.length - 1] === sample) return;
    if (this.fallbackSrcTrail.length >= 3) return;

    this.fallbackSrcTrail.push(sample);
    this.muxUpdateData({ custom_4: `fallback_src:${this.fallbackSrcTrail.join(">")}` });
  }

  // Deliberately lossy: the signed fallback URL carries a Signature query param, so only a short
  // discriminator (never the URL itself, never a token) goes into telemetry.
  describeSource(src) {
    if (!src) return "empty";
    if (src.startsWith("blob:")) return "hlsjs-blob";

    let url, original;
    try {
      url = new URL(src, window.location.href);
      original = this.originalUrl ? new URL(this.originalUrl, window.location.href) : null;
    } catch {
      return "unparseable";
    }

    // Both blanks: Plyr's CDN default (still the fallback when the value is blank) and our
    // self-hosted asset. Pathname-only compare - Propshaft digests the filename, so the
    // rendered URL isn't string-equal to the value we handed Plyr on a stale cached page.
    if (url.hostname === "cdn.plyr.io") return "plyr-blank";
    if (this.blankVideoUrlValue && url.pathname === new URL(this.blankVideoUrlValue, window.location.href).pathname) return "plyr-blank";
    // Host + path only: the query string is where the CloudFront signature lives.
    if (original && url.host === original.host && url.pathname === original.pathname) return "signed-mp4";

    return `other:${url.hostname}`;
  }

  initializePlayer() {
    this.player = new Plyr(this.video, {
      autoplay: this.autoplayValue ?? true,
      // Plyr assigns blankVideo on every reset/destroy, so its default
      // (https://cdn.plyr.io/static/blank.mp4) means every teardown hits a third-party CDN we
      // don't control - and that URL is what shows up as the source in Mux code-4 beacons.
      // Self-hosted instead; a blank value leaves Plyr's own default in place.
      ...(this.blankVideoUrlValue ? { blankVideo: this.blankVideoUrlValue } : {}),
      seekTime: 15,
      fullscreen: { enabled: true, iosNative: true },
      volume: 1,
      controls: [
        'play-large', // The large play button in the center
        this.isLive() ? null : 'rewind', // Rewind by seekTime (not for live)
        'play', // Play/pause playback
        this.isLive() ? null : 'fast-forward', // Fast forward by seekTime, clamped to the party playhead during premieres (not for live)
        this.isLive() ? null : 'progress', // The progress bar and scrubber (not for live)
        this.isLive() ? null : 'current-time', // The current time of playback (not for live)
        'duration', // Full duration (VOD) / elapsed live time (live)
        'mute', // Toggle mute
        'volume', // Volume control
        'airplay', // Airplay (currently Safari only)
        this.isLive() || this.isPremiere() ? null : 'pip', // Picture-in-picture (not for live/premiere)
        this.video.querySelector("track[kind='captions']") ? 'captions' : null, // Captions
        'fullscreen', // Toggle fullscreen
      ].flat()
    });

    this.injectControls();
    this.adjustNativeControlIcons();

    this.addMobileEnhancements();
    this.initializePictureInPicture();
    this.setMediaSessionActionHandlers();
  }

  addMobileEnhancements() {
    // Rely on desktops not having orientation support for detection
    if (!screen.orientation) {
      return;
    }

    this.player.on("enterfullscreen", () => {
      screen.orientation.lock("landscape");
    });

    this.player.on("exitfullscreen", () => {
      screen.orientation.unlock();
    });

    // Live and premiere have no playback-speed control, so their rate-toggle targets are absent.
    if (!this.isLive() && !this.isPremiere()) {
      this.player.on("ratechange", this.onRateChange.bind(this));
    }

    this.video.addEventListener("webkitfullscreenchange", () => {
      if (this.video.webkitDisplayingFullscreen) {
        screen.orientation.lock("landscape");
      } else {
        screen.orientation.unlock();
      }
    });
  }

  initializePictureInPicture() {
    if (!document.pictureInPictureEnabled) {
      return;
    }
    this.video.addEventListener("leavepictureinpicture", this.leavePictureInPicture.bind(this));

    this.pipEnabled = true;

    // Chrome requires readyState >= HAVE_METADATA (1) before requestPictureInPicture() can be
    // called. Disable the PiP button until the video element has loaded its metadata, so an
    // eager click before the video is ready doesn't throw an InvalidStateError.
    const pipButton = this.player.elements.container?.querySelector('[data-plyr="pip"]');
    if (pipButton && this.video.readyState < HTMLMediaElement.HAVE_METADATA) {
      pipButton.disabled = true;
      this.video.addEventListener(
        "loadedmetadata",
        () => { pipButton.disabled = false; },
        { once: true }
      );
    }
  }


  injectControls() {
    const controls = document.importNode(this.controlsTemplateTarget.content, true);
    const rewindIcon = controls.querySelector('[data-video-icon="rewind"]');
    const forwardIcon = controls.querySelector('[data-video-icon="fast-forward"]');

    // If relocated, the controls element will be null - this is fine since we've already injected the controls
    if (this.player.elements.controls) {
      this.player.elements.controls.appendChild(controls);
      this.swapSeekIcon('rewind', rewindIcon);
      this.swapSeekIcon('fast-forward', forwardIcon);
    }
  };

  // Plyr renders its native fullscreen button before our appended custom controls - move it
  // to the end so playback-rate/cast/theater sort ahead of it as intended. Also shrink the
  // fullscreen/pip icons: Plyr's stylesheet sizes every control svg via the
  // --plyr-control-icon-size custom property (18px default), which Tailwind classes can't
  // override without !important - setting the variable inline wins the cascade instead, and
  // 12px was confirmed visually to match the weight of the theater/cast icons next to them.
  adjustNativeControlIcons() {
    const fullscreenButton = this.element.querySelector('[data-plyr="fullscreen"]');
    const pipButton = this.element.querySelector('[data-plyr="pip"]');

    if (fullscreenButton) fullscreenButton.parentElement.appendChild(fullscreenButton);
    [fullscreenButton, pipButton].forEach(btn => btn && btn.style.setProperty('--plyr-control-icon-size', '12px'));
  }

  // Plyr renders default double-triangle icons for these from its own CDN sprite -
  // swap in the same custom "15s" icon the audio player uses instead.
  swapSeekIcon(plyrControl, iconHolder) {
    if (!iconHolder) return;
    const button = this.player.elements.controls.querySelector(`[data-plyr="${plyrControl}"]`);
    const svg = button?.querySelector('svg');
    if (svg) {
      svg.replaceWith(iconHolder.querySelector('svg'));
    }
    iconHolder.remove();
  }

  updateTime() {
    const event = new SupercastTimeUpdateEvent({
      itemId: this.element.getAttribute('data-item-id'),
      itemType: this.element.getAttribute('data-item-type'),
      currentTime: this.player.currentTime,
      duration: this.player.duration,
      isPlaying: !this.player.paused,
      mediaType: "video"
    });

    this.updateMediaSessionPlaybackState();

    window.dispatchEvent(event);
  }

  isFloating() {
    return this.element.parentElement.id == "floating-player";
  }

  leavePictureInPicture(event) {
    // Live and premiere expose no PiP control, so there's no feed-item page to return to - leave the player in place.
    if (this.isLive() || this.isPremiere()) {
      return;
    }

    // If users hit the 'X' button (rather than return to frame), the video will be automatically paused beforehand
    // there's no other way to detect the difference. In that case we shouldn't try to navigate back to the video.
    // (This adds an edge case where pausing and then hitting 'return to frame' will not navigate back to the video,
    // but the alternative of navigating on 'X' is worse)
    if (this.player.paused) {
      return;
    }

    if (window.location.pathname != this.thisVideoPath()) {
      this.player.pip = false;
      this.player.pause();
      event.preventDefault();
      window.Turbo.visit(`/subscriber_v2/feed_items/${this.element.getAttribute('data-feed-item-id')}?timestamp=${this.player.currentTime}`);
      this.close();
    }
  }

  disconnect() {
    this.stopCastPolling();
    this.stopPremiereEdgeUpdates();
    clearTimeout(this.hlsRetryTimeoutId);
    clearTimeout(this.masterManifestPollTimeoutId);
    window.removeEventListener("supercast-timeupdate", this.boundOnTimeUpdate);
    window.removeEventListener("pageshow", this.boundRestoreTheaterState);

    // Don't destroy when in PiP - HLS.js must keep running to supply data to the floating window.
    if (!document.pictureInPictureElement || document.pictureInPictureElement !== this.video) {
      // Tell Mux the view is ending deliberately BEFORE the source goes away. Without this, an
      // ordinary Turbo navigation away reads to mux-embed's automaticErrorTracking as a
      // MEDIA_ERR_SRC_NOT_SUPPORTED playback failure, with watch time equal to what was watched -
      // the bulk of the false code-4 failure rate. Inside the PiP guard on purpose: playback
      // continues in the floating window, so ending the view there would be just as wrong.
      this.muxTeardown();
      this.hls?.destroy();
      this.player?.destroy();
    }
  }

  thisVideoPath() {
    return `/subscriber_v2/feed_items/${this.element.getAttribute('data-feed-item-id')}`;
  }

  close() {
    this.element.remove();
  }

  async toggleCast() {
    if (this.isCasting) {
      this.stopCasting();
    } else {
      this.cast();
    }
  }

  async cast() {
    const session = await this.getChromeCastSession(true);
    const mediaInfo = new chrome.cast.media.MediaInfo(this.originalUrl, this.fileTypeValue);
    const request = new chrome.cast.media.LoadRequest(mediaInfo);
    request.autoplay = true;
    session.loadMedia(request);
    this.isCasting = true;

    this.castPlaceholderTarget.classList.remove("hidden");
    this.videoContainerTarget.classList.add("hidden");
  }

  async stopCasting() {
    const castSession = await this.getChromeCastSession(false);
    if (castSession) {
      castSession.endSession(true);
    }
    this.isCasting = false;
    this.castPlaceholderTarget.classList.add("hidden");
    this.videoContainerTarget.classList.remove("hidden");
  }

  async readyToPlay() {
    this.video.removeEventListener('canplay', this.boundReadyToPlay);
    window.sessionStorage.removeItem(this.reloadGuardKey());

    // Live streams have no resume position or feed-token tracking - just play.
    if (this.isLive()) {
      this.player.on("play", this.seekToLiveEdge.bind(this));
      if (this.autoplayValue) {
        this.video.play();
      }
      return;
    }

    // Start at the shared playhead; never resume from playbackDb, so everyone lands on the same moment.
    if (this.isPremiere()) {
      this.video.currentTime = this.premiereLivePos();
      this.player.on("timeupdate", this.clampToPremiereEdge.bind(this));
      this.player.on("seeking", this.clampToPremiereEdge.bind(this));
      if (this.autoplayValue) {
        this.video.play();
      }
      return;
    }

    if (this.hasTimestampValue && this.timestampValue > 0) {
      this.video.currentTime = this.timestampValue;
    } else {
      const listenData = await window.playbackDb.getPlaybackData(this.element.getAttribute('data-item-type'), this.element.getAttribute('data-item-id'));
      if (listenData) {
        this.video.currentTime = listenData.currentTime;
      }
    }

    this.player.on("timeupdate", this.updateTime.bind(this));
    if (this.autoplayValue) {
      this.video.play();
    }

    // Update feed token connection status when video starts playing
    this.updateFeedTokenConnection();
  }

  updateFeedTokenConnection() {
    const episodeId = this.element.getAttribute('data-item-id');
    if (episodeId) {
      fetch(`/subscriber_v2/feed_items/mark_feed_token_connected?episode_id=${episodeId}`, {
        method: "POST"
      });
    }
  }

  updateMediaSessionPlaybackState() {
    if (!("mediaSession" in navigator)) {
      return;
    }

    navigator.mediaSession.setPositionState({
      duration: this.player.duration,
      playbackRate: this.player.speed,
      position: this.player.currentTime
    });

    navigator.mediaSession.playbackState = this.player.paused ? "paused" : "playing";

  }

  setMediaSessionActionHandlers() {
    if (!("mediaSession" in navigator)) {
      return;
    }

    navigator.mediaSession.setActionHandler("play", () => {
      this.play();
    });

    navigator.mediaSession.setActionHandler("pause", () => {
      this.pause();
    });

    navigator.mediaSession.setActionHandler("seekbackward", () => {
      this.player.currentTime -= 15;
    });

    navigator.mediaSession.setActionHandler("seekforward", () => {
      this.player.forward();
    });
  }

  initChromecast() {
    if (!window.chrome) { return; }
    // Held on the controller so disconnect() can stop polling when the SDK never becomes available.
    // chrome.cast itself loads asynchronously (the sender SDK script tag finishing doesn't mean
    // the namespace exists yet), so it's checked here on every tick rather than once up front -
    // otherwise a cold page load can race the SDK and never see the cast button appear.
    this.loadCastInterval = setInterval(() => {
      if (window.chrome.cast && window.chrome.cast.isAvailable) {
        this.stopCastPolling();
        this.initializeCastApi();
      }
    }, 1000);
  }

  stopCastPolling() {
    clearInterval(this.loadCastInterval);
    this.loadCastInterval = null;
  }

  async initializeCastApi() {
    this.castContext = cast.framework.CastContext.getInstance();
    this.castContext.setOptions({
      receiverApplicationId: chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
      autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED
    });
    this.castButtonTarget.classList.remove("hidden");
  }

  async getChromeCastSession(initializeNew) {
    const castSession = this.castContext.getCurrentSession();
    if (castSession) {
      return castSession;
    } else if (initializeNew) {
      await this.castContext.requestSession();
      return this.castContext.getCurrentSession();
    } else {
      return null;
    }
  }

  onTimeUpdate(event) {
    if (event.detail.mediaType === "audio" && this.hasFloatingValue && this.floatingValue) {
      this.close();
    }
  }

  // Duplicated from audio player - consider keeping in sync
  onRateChange() {
    if (!this.hasPlayback1Target) return;
    if (this.player.speed == 1) {
      this.playback1Target.classList.remove("!hidden")
      this.playback1_5Target.classList.add("!hidden")
      this.playback2Target.classList.add("!hidden")
    } else if (this.player.speed == 1.5) {
      this.playback1Target.classList.add("!hidden")
      this.playback1_5Target.classList.remove("!hidden")
      this.playback2Target.classList.add("!hidden")
    } else {
      this.playback1Target.classList.add("!hidden")
      this.playback1_5Target.classList.add("!hidden")
      this.playback2Target.classList.remove("!hidden")
    }
  }

  // Duplicated from audio player - consider keeping in sync
  togglePlaybackRate() {
    if (this.player.speed == 1) {
      this.player.speed = 1.5;
    } else if (this.player.speed == 1.5) {
      this.player.speed = 2;
    } else {
      this.player.speed = 1;
    }
  }

  toggleTheater() {
    if (this.isFloating()) return;

    writeTheaterCookie(document.body.toggleAttribute("data-theater"));
  }

  // Turbo restoration visits reuse a cached <body> without re-requesting, so the server-rendered
  // state can be stale on back/forward - re-derive it from the cookie whenever the player attaches.
  syncTheaterState() {
    if (this.isFloating() || !this.hasTheaterButtonTarget) return;

    document.body.toggleAttribute("data-theater", theaterCookieEnabled());
  }

  restoreTheaterState(event) {
    if (event.persisted) this.syncTheaterState();
  }
}
